import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  SparklesIcon,
  BuildingOfficeIcon,
  CodeBracketIcon,
  DevicePhoneMobileIcon,
  ServerIcon,
  CpuChipIcon,
  CloudIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  UserGroupIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

const AttendMainPage: React.FC = () => {
  const [showFloatingButton, setShowFloatingButton] = useState(false);

  // 내부망/로컬에서 독서실 서비스 링크 동적 생성
  const getStudyRoomUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === '192.168.0.38') {
      return '/studyroom'; // 내부망에서는 경로로 접근
    }
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '/studyroom'; // 로컬에서는 /studyroom 경로로 접근
    }
    return 'https://studyroom.newattend.com'; // 외부에서는 서브도메인
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowFloatingButton(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* 헤더 */}
      <header className="fixed top-0 left-0 right-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="w-full">
          <div style={{
            position: 'relative',
            height: '64px',
            width: '100%',
            padding: '0 2rem'
          }}>
            {/* 왼쪽 영역 - 로고 + 브랜드명 */}
            <div style={{
              position: 'absolute',
              left: '2rem',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <img
                src="/logo-ntd.png"
                alt="NTD 로고"
                className="rounded-lg"
                style={{
                  width: '40px',
                  height: '40px',
                  objectFit: 'contain'
                }}
              />
              <h1 className="text-xl font-semibold text-gray-900 whitespace-nowrap">
                NTD
              </h1>
            </div>

            {/* 중앙 영역 - 메뉴 */}
            <nav style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '3rem',
              minWidth: 'fit-content'
            }} className="hidden lg:flex">
              <a href="#services" className="text-gray-600 hover:text-gray-900 font-medium transition-colors whitespace-nowrap">
                서비스
              </a>
              <a href="#portfolio" className="text-gray-600 hover:text-gray-900 font-medium transition-colors whitespace-nowrap">
                제품
              </a>
              <a href="#contact" className="text-gray-600 hover:text-gray-900 font-medium transition-colors whitespace-nowrap">
                문의
              </a>
            </nav>

            {/* 오른쪽 영역 */}
            <div style={{
              position: 'absolute',
              right: '2rem',
              top: '50%',
              transform: 'translateY(-50%)'
            }}>
              <Link
                to="/admin/login"
                className="px-3 py-2 text-white text-sm font-medium rounded-sm transition-colors whitespace-nowrap"
                style={{ backgroundColor: '#232D3F' }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#232D3F'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#232D3F'}
              >
                관리자
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* 히어로 섹션 */}
      <section className="pt-20 sm:pt-24 pb-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
              비즈니스에 맞는<br /><span style={{ color: '#232D3F' }}>시스템을 설계합니다</span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-4">
              복잡한 업무 프로세스를 분석하고, 효율적인 시스템으로 설계합니다.
              <br className="hidden sm:inline" />
              설계된 시스템은 웹·앱·AI 등 최적의 방법으로 구현해 드립니다.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-12 sm:mb-16 px-4">
              <a
                href="#contact"
                className="inline-flex items-center justify-center px-6 sm:px-8 py-3 text-white text-sm sm:text-base font-medium rounded-sm transition-colors"
                style={{ backgroundColor: '#232D3F' }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#232D3F'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#232D3F'}
              >
                상담 신청하기
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </a>
              <a
                href="#portfolio"
                className="inline-flex items-center justify-center px-6 sm:px-8 py-3 border border-gray-300 text-gray-700 text-sm sm:text-base font-medium rounded-sm hover:bg-gray-50 transition-colors"
              >
                제품 둘러보기
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 서비스 - 시스템 설계 */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4 tracking-tight">
              우리가 하는 일
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              비즈니스를 이해하고, 문제를 해결하는 시스템을 설계합니다
            </p>
          </div>

          {/* 메인: 시스템 설계 */}
          <div className="rounded-2xl p-8 md:p-12 mb-12" style={{ background: 'linear-gradient(to bottom right, #E8EAED, #F0F1F3)' }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ backgroundColor: '#E8EAED', color: '#232D3F' }}>
                  <ChartBarIcon className="w-4 h-4" />
                  핵심 서비스
                </div>
                <h4 className="text-3xl font-bold text-gray-900 mb-4">비즈니스 시스템 설계</h4>
                <p className="text-gray-600 text-lg mb-6">
                  "프로그램 만들어주세요"가 아니라<br />
                  <strong>"이 문제를 어떻게 해결할까요?"</strong>부터 시작합니다.
                </p>
                <p className="text-gray-600 mb-8">
                  현재 업무 방식을 분석하고, 비효율을 찾아내고,<br />
                  최적의 시스템 구조를 설계해 드립니다.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#232D3F' }} />
                    <div>
                      <div className="font-semibold text-gray-900">업무 프로세스 분석</div>
                      <div className="text-sm text-gray-600">현재 업무 흐름을 파악하고 개선점을 찾습니다</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#232D3F' }} />
                    <div>
                      <div className="font-semibold text-gray-900">시스템 구조 설계</div>
                      <div className="text-sm text-gray-600">데이터 흐름, 화면 구성, 기능 정의를 설계합니다</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircleIcon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#232D3F' }} />
                    <div>
                      <div className="font-semibold text-gray-900">확장 가능한 아키텍처</div>
                      <div className="text-sm text-gray-600">비즈니스 성장에 맞춰 확장할 수 있는 구조를 만듭니다</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="text-sm text-gray-500 mb-4">설계 프로세스</div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 rounded-lg" style={{ backgroundColor: '#E8EAED' }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: '#232D3F' }}>1</div>
                      <div className="font-medium text-gray-900">현황 파악 & 요구사항 정리</div>
                    </div>
                    <div className="flex items-center gap-4 p-4 rounded-lg" style={{ backgroundColor: '#E8EAED' }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: '#232D3F' }}>2</div>
                      <div className="font-medium text-gray-900">프로세스 분석 & 개선안 도출</div>
                    </div>
                    <div className="flex items-center gap-4 p-4 rounded-lg" style={{ backgroundColor: '#E8EAED' }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: '#232D3F' }}>3</div>
                      <div className="font-medium text-gray-900">시스템 구조 & 화면 설계</div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white font-bold text-sm">4</div>
                      <div className="font-medium text-gray-500">프로그램 구현 (선택)</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 서브: 구현 방법 */}
          <div className="text-center mb-8">
            <p className="text-gray-500">설계된 시스템은 다양한 방법으로 구현할 수 있습니다</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-5 text-center">
              <CodeBracketIcon className="h-8 w-8 text-gray-400 mx-auto mb-3" />
              <div className="font-medium text-gray-700">웹 시스템</div>
              <div className="text-xs text-gray-500 mt-1">관리자 페이지, SaaS</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-5 text-center">
              <DevicePhoneMobileIcon className="h-8 w-8 text-gray-400 mx-auto mb-3" />
              <div className="font-medium text-gray-700">모바일 앱</div>
              <div className="text-xs text-gray-500 mt-1">Android, iOS</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-5 text-center">
              <CpuChipIcon className="h-8 w-8 text-gray-400 mx-auto mb-3" />
              <div className="font-medium text-gray-700">AI / 자동화</div>
              <div className="text-xs text-gray-500 mt-1">얼굴인식, 챗봇</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-5 text-center">
              <ServerIcon className="h-8 w-8 text-gray-400 mx-auto mb-3" />
              <div className="font-medium text-gray-700">백엔드 / API</div>
              <div className="text-xs text-gray-500 mt-1">서버, 데이터베이스</div>
            </div>
          </div>
        </div>
      </section>

      {/* NTD의 제품 */}
      <section id="portfolio" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4 tracking-tight">
              NTD의 제품
            </h3>
            <p className="text-lg text-gray-600">
              직접 설계하고, 직접 운영하는 실전 검증된 시스템
            </p>
          </div>

          {/* 제품 카드 2개 동등 배치 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            {/* 제품 1: NewAttend */}
            <div className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow" style={{ background: 'linear-gradient(135deg, #1a1f36 0%, #232D3F 100%)' }}>
              {/* 스크린샷 */}
              <div className="flex justify-center pt-8 pb-4 px-8">
                <div className="bg-gray-900 rounded-2xl p-2 shadow-xl">
                  <div className="rounded-xl overflow-hidden w-44">
                    <img
                      src="/app-screenshot-face.png"
                      alt="NewAttend AI 얼굴인식 화면"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>

              <div className="p-8 pt-4 text-white">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium mb-4">
                  <CpuChipIcon className="w-4 h-4" />
                  AI 얼굴인식 출결 프로그램
                </div>
                <h4 className="text-3xl font-bold mb-3">NewAttend</h4>
                <p className="text-base mb-6" style={{ color: '#A0AEC0' }}>
                  카드 없이, 비밀번호 없이 — 얼굴만으로 출결을 관리합니다.
                  1~1.5초 만에 본인 확인 완료.
                </p>

                {/* 핵심 수치 */}
                <div className="grid grid-cols-4 gap-2 mb-6">
                  <div className="bg-white/10 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-blue-300">1.5초</p>
                    <p className="text-xs" style={{ color: '#A0AEC0' }}>인식</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-blue-300">99%+</p>
                    <p className="text-xs" style={{ color: '#A0AEC0' }}>정확도</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-blue-300">0원</p>
                    <p className="text-xs" style={{ color: '#A0AEC0' }}>카드비</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-blue-300">24h</p>
                    <p className="text-xs" style={{ color: '#A0AEC0' }}>무인</p>
                  </div>
                </div>

                {/* 주요 기능 */}
                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span>AI 얼굴인식 입실·퇴실 자동 체크</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span>학부모 카카오 알림톡 자동 발송</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span>자동문·도어락 연동 (완전 무인 출입)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span>좌석·이용권·결제·통계 대시보드</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span>관리형 독서실 특화 기능 포함</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <a
                    href={getStudyRoomUrl()}
                    className="inline-flex items-center px-5 py-2.5 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-colors text-sm"
                  >
                    서비스 보기
                    <ArrowRightIcon className="ml-1.5 h-4 w-4" />
                  </a>
                  <a
                    href="#contact"
                    className="inline-flex items-center px-5 py-2.5 border border-white/30 text-white font-medium rounded-lg hover:bg-white/10 transition-colors text-sm"
                  >
                    도입 상담
                  </a>
                </div>
              </div>
            </div>

            {/* 제품 2: NTD 매니저 */}
            <div className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow bg-white border border-gray-100">
              {/* 시각 영역 */}
              <div className="flex justify-center pt-8 pb-4 px-8" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)' }}>
                <div className="w-44 h-44 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#d1fae5' }}>
                  <div className="text-center">
                    <UserGroupIcon className="w-16 h-16 text-emerald-600 mx-auto mb-2" />
                    <p className="text-sm font-bold text-emerald-700">CRM</p>
                  </div>
                </div>
              </div>

              <div className="p-8 pt-4">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-4">
                  <UserGroupIcon className="w-4 h-4" />
                  거래처·고객 관리 CRM
                </div>
                <h4 className="text-3xl font-bold text-gray-900 mb-3">NTD 매니저</h4>
                <p className="text-base text-gray-600 mb-6">
                  거래처, 계약, 출장보고서, 매출까지 한곳에서 관리하는
                  비즈니스 CRM 시스템.
                </p>

                {/* 주요 기능 */}
                <div className="space-y-2 mb-6 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-gray-700">거래처·담당자 정보 통합 관리</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-gray-700">계약 관리 + 세금계산서 연동</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-gray-700">출장 보고서 작성·관리 (사진 첨부)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-gray-700">매출·지출 재무 관리 대시보드</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-gray-700">일정·메모·팀 협업 기능</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <a
                    href="#contact"
                    className="inline-flex items-center px-5 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors text-sm"
                  >
                    도입 문의
                    <ArrowRightIcon className="ml-1.5 h-4 w-4" />
                  </a>
                  <span className="inline-flex items-center px-4 py-2.5 text-sm text-gray-500">실제 운영 중</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 출석부 실제 화면 */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-center text-sm font-medium text-gray-400 tracking-widest uppercase mb-4">실제 운영 화면</p>
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-3 tracking-tight">
            한 화면에서 모든 출결을 관리합니다
          </h3>
          <p className="text-center text-gray-600 mb-10">
            출석 · 외출 · 미출석 · 곧 입실 — 70명의 상태를 실시간으로 확인
          </p>
          <div className="rounded-2xl overflow-hidden shadow-xl border border-gray-200">
            <img
              src="/screenshot-attendance.png"
              alt="NewAttend 출석부 실시간 관리 화면"
              className="w-full"
            />
          </div>
        </div>
      </section>

      {/* 왜 NTD인가 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4 tracking-tight">
              왜 NTD인가
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#E8EAED' }}>
                <WrenchScrewdriverIcon className="h-8 w-8" style={{ color: '#232D3F' }} />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">직접 만들고 직접 운영</h4>
              <p className="text-gray-600">
                이론이 아닌 실전.<br />
                우리가 만든 시스템을 직접 운영합니다
              </p>
            </div>

            <div className="text-center p-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#E8EAED' }}>
                <UserGroupIcon className="h-8 w-8" style={{ color: '#232D3F' }} />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">밀착 소통</h4>
              <p className="text-gray-600">
                담당자와 직접 소통하며<br />
                요구사항을 정확히 반영합니다
              </p>
            </div>

            <div className="text-center p-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#E8EAED' }}>
                <ShieldCheckIcon className="h-8 w-8" style={{ color: '#232D3F' }} />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">지속 지원</h4>
              <p className="text-gray-600">
                출시 후에도 유지보수와<br />
                기능 개선을 지원합니다
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 도입 사례 섹션 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-center text-sm font-medium text-gray-400 tracking-widest uppercase mb-10">도입 사례</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8EAED' }}>
                  <BuildingOfficeIcon className="w-5 h-5" style={{ color: '#232D3F' }} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">대구 소재 고등학교</p>
                  <p className="text-xs text-gray-400">교육기관 · 학생 출결 관리</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                "체계적으로 보이고, <span className="text-gray-900 font-medium">한눈에 관리할 수 있어서 너무 좋습니다.</span>"
              </p>
              <p className="text-xs text-gray-400 mt-2">— 교감 선생님</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8EAED' }}>
                  <UserGroupIcon className="w-5 h-5" style={{ color: '#232D3F' }} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">광교 소재 독서실</p>
                  <p className="text-xs text-gray-400">독서실 · 회원 출결 관리</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                "항상 수기로 출석부를 작성했는데, 프로그램으로 하니 <span className="text-gray-900 font-medium">일손이 많이 줄었습니다.</span>"
              </p>
              <p className="text-xs text-gray-400 mt-2">— 독서실 원장님</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section id="contact" className="py-20" style={{ backgroundColor: '#232D3F' }}>
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h3 className="text-4xl font-bold text-white mb-6 tracking-tight">
            비즈니스에 맞는 시스템이 필요하신가요?
          </h3>
          <p className="text-lg mb-10 max-w-3xl mx-auto" style={{ color: '#A0AEC0' }}>
            막연한 아이디어도 괜찮습니다.<br />
            비즈니스를 분석하고, 시스템을 설계하고, 프로그램으로 만들어 드립니다.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-base font-medium rounded-sm hover:bg-gray-50 transition-colors"
              style={{ color: '#232D3F' }}
            >
              무료 상담 신청
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </Link>
            <a
              href="mailto:newattend@naver.com"
              className="inline-flex items-center justify-center px-8 py-3 border text-white text-base font-medium rounded-sm transition-colors"
              style={{ borderColor: '#4A5568' }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#232D3F'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              이메일 문의
            </a>
          </div>
        </div>
      </section>

      {/* 푸터 */}
      <footer className="bg-gray-50 border-t border-gray-200 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center mb-4">
                <img
                  src="/logo-ntd.png"
                  alt="NTD 로고"
                  className="rounded-lg"
                  style={{ width: '28px', height: '28px' }}
                />
                <span className="ml-3 text-xl font-semibold text-gray-900">
                  NTD
                </span>
              </div>
              <p className="text-gray-600 mb-6 max-w-md">
                비즈니스에 맞는 시스템을 설계합니다.<br />
                설계된 시스템은 프로그램으로 구현해 드립니다.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-4">제품</h4>
              <div className="space-y-3">
                <a href="#portfolio" className="block text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  NewAttend (AI 얼굴인식 출결)
                </a>
                <a href={getStudyRoomUrl()} className="block text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  관리형 독서실
                </a>
                <a href="#portfolio" className="block text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  NTD 매니저 (CRM)
                </a>
                <a href="#services" className="block text-gray-600 hover:text-gray-900 text-sm transition-colors">
                  맞춤 시스템 설계
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 mt-12 pt-8">
            <div className="mb-6">
              <p className="text-gray-600 text-sm mb-2">
                <span className="font-semibold">상호:</span> 뉴어텐드 | <span className="font-semibold">대표자:</span> 배남수 | <span className="font-semibold">사업자등록번호:</span> 685-12-02762
              </p>
              <p className="text-gray-600 text-sm mb-2">
                <span className="font-semibold">주소:</span> 경기도 광주시 문형산길47번길 62-14, 202호
              </p>
              <p className="text-gray-600 text-sm">
                <span className="font-semibold">이메일:</span> newattend@naver.com | <span className="font-semibold">상담시간:</span> 평일 9:00-18:00
              </p>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-500 text-sm">
                © 2024 엔티디(NTD). All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* 플로팅 버튼 - NewAttend 바로가기 */}
      <a
        href={getStudyRoomUrl()}
        className={`fixed right-6 bottom-6 z-50 flex items-center justify-center w-16 h-16 text-white rounded-full shadow-lg hover:shadow-xl transform transition-all duration-300 group ${
          showFloatingButton ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
        }`}
        style={{ backgroundColor: '#232D3F' }}
        title="NewAttend 서비스 바로가기"
      >
        <CpuChipIcon className="h-8 w-8" />
        <span className="absolute right-full mr-3 bg-gray-900 text-white text-sm font-medium px-3 py-2 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          NewAttend 서비스
        </span>
      </a>
    </div>
  );
};

export default AttendMainPage;
